import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Drawer, Empty, Modal, Space, Switch, Tag, message, theme } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useAuth } from '@/contexts/AuthContext'
import { useSimulazione } from '@/contexts/SimulazioneContext'
import { leggiPermessiConsultati, sottoscriviRaccolta } from '@/lib/raccoltaPermessi'

/**
 * PannelloPermessi — quali permessi governano la schermata che hai davanti.
 *
 * Elenca i permessi che i componenti montati hanno consultato dall'ultimo
 * cambio di percorso (le modali aperte dopo si aggiungono), con nome e
 * descrizione presi dalla matrice, e segna quali mancano al ruolo in vigore.
 *
 * Durante la simulazione ogni permesso mancante ha un interruttore "Prova":
 * lo aggiunge ai permessi simulati in memoria e la pagina si ridisegna.
 * Scrivere davvero e' un gesto separato, con conferma.
 */

interface Props {
  open: boolean
  onClose: () => void
}

export const PannelloPermessi: React.FC<Props> = ({ open, onClose }) => {
  const { token } = theme.useToken()
  const { permissions } = useAuth()
  const {
    ruoloSimulato,
    permessiMatrice,
    permessiInProva,
    aggiungiProva,
    rimuoviProva,
    salvaProveNellaMatrice,
  } = useSimulazione()

  const [mostraCodici, setMostraCodici] = useState(false)
  const [salvataggio, setSalvataggio] = useState(false)
  // Ridisegna quando la raccolta cambia; la sottoscrizione esiste solo a
  // pannello aperto, cosi' a pannello chiuso registrare non costa nulla.
  const [, forzaAggiornamento] = useState(0)

  useEffect(() => {
    if (!open) return
    return sottoscriviRaccolta(() => forzaAggiornamento(n => n + 1))
  }, [open])

  const consultati = open ? leggiPermessiConsultati() : []

  // Raggruppa per categoria, usando l'anagrafica della matrice.
  const categorie = useMemo(() => {
    const gruppi = new Map<string, typeof permessiMatrice>()
    const sconosciuti: string[] = []
    for (const codice of [...consultati].sort()) {
      const perm = permessiMatrice.find(p => p.codice === codice)
      if (!perm) {
        sconosciuti.push(codice)
        continue
      }
      const elenco = gruppi.get(perm.categoria)
      if (elenco) elenco.push(perm)
      else gruppi.set(perm.categoria, [perm])
    }
    return { gruppi: Array.from(gruppi.entries()), sconosciuti }
  }, [consultati.join('|'), permessiMatrice])

  const haPermesso = (codice: string) => permissions.includes(codice)

  const conferma = () => {
    if (!ruoloSimulato || permessiInProva.length === 0) return
    Modal.confirm({
      title: 'Salvare nella matrice?',
      width: 520,
      content: (
        <div style={{ fontSize: 13 }}>
          <p style={{ marginTop: 0 }}>
            Verranno concessi al ruolo <strong>{ruoloSimulato.nome}</strong> ({ruoloSimulato.codice}){' '}
            {permessiInProva.length === 1 ? 'questo permesso' : `questi ${permessiInProva.length} permessi`}:
          </p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {permessiInProva.map(codice => {
              const perm = permessiMatrice.find(p => p.codice === codice)
              return (
                <li key={codice}>
                  {perm?.nome ?? codice}{' '}
                  <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>({codice})</span>
                </li>
              )
            })}
          </ul>
          <p style={{ marginBottom: 0, color: token.colorTextSecondary }}>
            Gli altri ruoli e gli altri permessi restano invariati.
          </p>
        </div>
      ),
      okText: 'Salva nella matrice',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          setSalvataggio(true)
          const quanti = await salvaProveNellaMatrice()
          message.success(
            `${quanti} ${quanti === 1 ? 'permesso concesso' : 'permessi concessi'} a ${ruoloSimulato.codice}.`,
          )
        } catch {
          message.error('Salvataggio non riuscito.')
        } finally {
          setSalvataggio(false)
        }
      },
    })
  }

  const rigaPermesso = (perm: typeof permessiMatrice[number]) => {
    const concesso = haPermesso(perm.codice)
    const inProva = permessiInProva.includes(perm.codice)
    return (
      <div
        key={perm.codice}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '10px 4px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: token.colorText }}>
            {perm.nome}
            {concesso ? (
              <Tag color="success" style={{ marginLeft: 6, fontSize: 10 }}>concesso</Tag>
            ) : (
              <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>mancante</Tag>
            )}
            {inProva && (
              <Tag color="blue" style={{ marginLeft: 2, fontSize: 10 }}>in prova</Tag>
            )}
          </div>
          {perm.descrizione && (
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 2 }}>
              {perm.descrizione}
            </div>
          )}
          {mostraCodici && (
            <div
              style={{
                fontSize: 11,
                marginTop: 2,
                color: token.colorTextTertiary,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {perm.codice}
            </div>
          )}
        </div>
        {ruoloSimulato && !concesso && (
          <Space direction="vertical" size={0} align="center">
            <Switch size="small" checked={inProva} onChange={v => (v ? aggiungiProva(perm.codice) : rimuoviProva(perm.codice))} />
            <span style={{ fontSize: 10, color: token.colorTextTertiary }}>Prova</span>
          </Space>
        )}
        {ruoloSimulato && inProva && concesso && (
          <Switch size="small" checked onChange={() => rimuoviProva(perm.codice)} />
        )}
      </div>
    )
  }

  return (
    <Drawer
      title="Permessi di questa schermata"
      placement="right"
      width={460}
      open={open}
      onClose={onClose}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Checkbox checked={mostraCodici} onChange={e => setMostraCodici(e.target.checked)}>
          <span style={{ fontSize: 12 }}>Mostra i codici tecnici</span>
        </Checkbox>

        {ruoloSimulato ? (
          <Alert
            type="info"
            showIcon
            message={`Permessi in vigore: ${ruoloSimulato.nome} (${ruoloSimulato.codice})`}
            description="Accendi Prova su un permesso mancante per vedere la pagina con quel permesso. Resta in memoria: non e' salvato."
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Permessi in vigore: i tuoi"
            description="Attiva una simulazione dal selettore per provare i permessi di un altro ruolo."
          />
        )}

        {permessiInProva.length > 0 && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={salvataggio}
            onClick={conferma}
            block
          >
            Salva nella matrice ({permessiInProva.length})
          </Button>
        )}

        {consultati.length === 0 && (
          <Empty description="Nessun permesso consultato da questa schermata" />
        )}

        {categorie.gruppi.map(([categoria, permessi]) => (
          <div key={categoria}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: '4px 6px',
                background: token.colorFillAlter,
                borderRadius: token.borderRadiusSM,
              }}
            >
              {categoria}
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: token.colorTextTertiary }}>
                {permessi.length}
              </span>
            </div>
            {permessi.map(rigaPermesso)}
          </div>
        ))}

        {categorie.sconosciuti.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Permessi consultati ma assenti dalla matrice"
            description={categorie.sconosciuti.join(', ')}
          />
        )}
      </Space>
    </Drawer>
  )
}

export default PannelloPermessi
